import {AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {Router} from "@angular/router";
import {
  HISTORY_KEY_SEARCH_TOTAL,
  LIST_TYPE,
  SearchParam
} from '../shared/defines/client.const';
import {first, Observable, Subject, timer, concat} from "rxjs";
import {delay, filter, mapTo, repeatWhen, switchMap} from "rxjs/operators";

import {UserService} from "../shared/services/user/user.service";
import {GREETING} from "../shared/defines/protocol";
import {
  DEFAULT_GREETING_MESSAGE,
  DEFAULT_GREETING_TITLE,
  GreetingService
} from "../shared/services/greeting/greeting.service";
import {GlobalEventService} from "../shared/services/global-event/global-event.service";

const GREETING_ARROW_SHOW = 5000;    // 인사말 화살표 표시 시간
const GREETING_ARROW_HIDE = 8000;   // 인사말 화살표 숨김 시간

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class MainComponent implements OnInit, AfterViewInit {

  // 화면 사이즈 맞춤에 사용
  @ViewChild('mainIntroBox') mainIntroBox?: ElementRef;
  @ViewChild('bushes') bushes?: ElementRef;
  @ViewChild('boyGirl') boyGirl?: ElementRef;

  LIST_TYPE = LIST_TYPE;
  protected readonly HISTORY_KEY_SEARCH_WORD = HISTORY_KEY_SEARCH_TOTAL;

  // 검색창에 넣는 값
  public keyword: string | null = null;
  public placeholder: string = "이 곳의 내용, 무엇이든 검색!";
  protected scrollTop = true;
  protected readonly HISTORY_KEY_SEARCH_TOTAL = HISTORY_KEY_SEARCH_TOTAL;

  private greetingClick$ = new Subject<void>();
  protected greeting: GREETING | null = null;

  // showHandArrow를 Observable로 선언 (async 파이프를 통해 구독)
  public showHandArrow$!: Observable<boolean>;

  constructor(
    private router: Router,
    private userSvc: UserService,
    private globalEventSvc: GlobalEventService,
    private greetingSvc: GreetingService,
  ) {

    // 인사말 처리 (인사말을 반복적으로 눌러도 이전 호출이 자동으로 취소되도록 switchMap 사용)
    this.greetingClick$.pipe(
      switchMap(() => {
        // 여기서 userService.GetGreeting() 호출
        return this.greetingSvc.GetGreeting();
      })
    ).subscribe(
      greeting => {
        // 성공적인 응답을 처리
        console.log('GetGreeting() greeting=', greeting);
        this.greeting = greeting;
      },
      err => {
        // 에러 발생 시 처리
        console.error(err);
        this.greeting = {
          title: DEFAULT_GREETING_TITLE,
          message: DEFAULT_GREETING_MESSAGE,
        };
      }
    );

    this.userSvc.Login().pipe(
      filter(value => value === true),
      first(),
    ).subscribe(() => {
      this.greetingClick$.next();
    });
  }

  ngOnInit(): void {
    // RxJS를 사용하여 비동기 객체로 손 모양 이미지 숨김/표시 사이클을 설정
    this.showHandArrow$ = concat(
      // 시작 시 즉시 false를 emit하여 이미지를 숨김
      timer(0).pipe(mapTo(false)),
      // GREETING_ARROW_HIDE 시간 후 true를 emit하여 이미지를 표시
      timer(GREETING_ARROW_HIDE).pipe(mapTo(true))
    ).pipe(
      // concat이 완료된 후 GREETING_ARROW_SHOW 시간 지연 후 반복
      repeatWhen((notifications) => notifications.pipe(delay(GREETING_ARROW_SHOW)))
    );
  }

  ngAfterViewInit() {
    this.updateExtHeight();

    // 최초 로딩 시, 화면 배치가 어그러지는 것을 고치기위한 코드. (TODO: 효과 있는지 확인 후 필요시 제거)
    timer(2000).subscribe(() => {
      this.updateExtHeight();
    });
  }


  onSearch(param: SearchParam) {
    if ('keyword' in param) {
      this.router.navigate(['/total'], {
        queryParams: {...param}
      }).then();
    }
  }

  /****************************************************************************
   * 브라우저 크기 변경 처리.
   ****************************************************************************/


  // 'resize' 이벤트를 감지하고 이벤트 발생 시 updateHeight 메소드를 호출합니다.
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.updateExtHeight();
  }

  private updateExtHeight(): void {
    if (this.mainIntroBox) {
      const upperMargin = Math.max(40, (window.innerHeight - 800) /2.4);
      // console.warn('updateExtHeight() upperMargin=', window.innerHeight, upperMargin);
      this.mainIntroBox.nativeElement.style.setProperty('padding-top', upperMargin + 'px');
    }

    const BUSHES_HEIGHT = 406;
    const BUSHES_HEIGHT_MIN = 180;
    const BUSHES_HEIGHT_MAX = 300;
    const innerHeight = window.innerHeight;

    if (this.bushes) {
      const heightBushed = Math.min(BUSHES_HEIGHT_MAX, Math.max(BUSHES_HEIGHT_MIN, (innerHeight - 920) / 2.0 ));

      const hPercent = Math.floor(BUSHES_HEIGHT / heightBushed * 100);

      // console.warn('updateExtHeight() window.innerHeight=', window.innerHeight, heightBushed, hPercent );

      // console.warn('updateExtHeight() upperMargin=', window.innerHeight, upperMargin);
      this.bushes.nativeElement.style.setProperty('height', heightBushed + 'px');
      this.bushes.nativeElement.style.setProperty('background-size', '100% ' + hPercent + '%');
    }

    const BOY_BOTTOM_MIN = -51;
    const BOY_BOTTOM_MAX = 70;

    if (this.boyGirl) {
      const bottom = Math.min(BOY_BOTTOM_MAX, Math.max(BOY_BOTTOM_MIN, (innerHeight - 1350) / 2.0 ));

      // console.warn('updateExtHeight() window.innerHeight=', window.innerHeight, bottom );
      this.boyGirl.nativeElement.style.setProperty('bottom', bottom + 'px');
    }
  }


  /****************************************************************************
   * 스크롤 처리
   ****************************************************************************/
  protected onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;

    // console.log('onScrollTop() ', target.scrollTop);
    this.scrollTop = target.scrollTop === 0; // 수직 스크롤 위치
  }

  /****************************************************************************
   * 인사말 처리
   ****************************************************************************/

  protected onClickGreeting($event: MouseEvent): void {
    console.warn('MainComponent::onClickHome()');
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event);

    this.greetingClick$.next();
  }
}
